import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { updateMeCoachOptionsSchema } from '@airreading/common/data';

import { EditableInfoCard, SubmitProps } from '@/components/editable-info-card';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputField } from '@/components/forms/form-input-field';
import { InfoKeyValue } from '@/components/info-key-value';
import { comboBoxOptionsOf } from '@/components/ui/multi-select-utils';
import { getMeCoach, updateMeCoach } from '@/lib/api-calls';
import { AllTimeZones, getTimezoneTypeahead, timezoneZodSchema } from '@/lib/time-zones';
import { Link } from '@tanstack/react-router';
import { CircleCheck, CircleX } from 'lucide-react';
import { ProfileFormProps } from './profile-form';

const formSchema = updateMeCoachOptionsSchema.extend({
  timeZone: timezoneZodSchema,
});
type FormState = z.infer<typeof formSchema>;

export const CoachProfileForm = ({ toast, queryClient, setTimezone, getSubmitErrorMessage }: ProfileFormProps) => {
  const { mutateAsync } = useMutation({
    mutationFn: updateMeCoach,
  });
  const { data: profile } = useSuspenseQuery(getMeCoach());
  const hasCoachRole = profile.userRoles.some((userRole) => userRole.role === 'coach');
  const currentTimeZone = profile.timeZone ?? Temporal.Now.timeZoneId();

  const onSubmit = ({ formData, disableEditing }: SubmitProps<FormState>) => {
    const timeZoneChanged = formData.timeZone.value !== profile.timeZone;

    return mutateAsync({
      ...formData,
      timeZone: formData.timeZone.value,
      // Update to current `teachingPreferences` value to avoid silent updates with wrong values
      teachingPreferences: profile.teachingPreferences || [],
    })
      .then(async ({ timeZone }) => {
        if (timeZone) {
          setTimezone(timeZone);
        }

        toast({
          title: 'Profile updated successfully',
        });
        disableEditing();
        await Promise.all([
          queryClient.refetchQueries({ queryKey: getMeCoach().queryKey }),
          timeZoneChanged ? queryClient.refetchQueries({ queryKey: ['COACH_AVAILABILITIES'] }) : Promise.resolve(),
        ]);
      })
      .catch((e: unknown) => {
        toast({
          title: getSubmitErrorMessage(e),
        });
      });
  };

  const smsReminderBaseClassnames = 'flex items-center justify-center w-14 h-10 rounded-md';

  return (
    <EditableInfoCard
      headerText={profile.fullNameOrEmail}
      formSchema={formSchema}
      defaultValues={{
        timeZone: {
          value: currentTimeZone,
          label: AllTimeZones.find((tz) => tz.value === currentTimeZone)?.label || currentTimeZone,
        },
        phone: profile.phone || '',
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        displayName: profile.displayName || '',
        smsReminderOptedIn: profile.smsReminderOptedIn,
        secondaryEmail: profile.secondaryEmail || '',
        currentTimeZone,
        teachingPreferences: profile.teachingPreferences || [],
      }}
      onSubmit={onSubmit}
      readonly={!hasCoachRole}
      showRedDot={false}
    >
      {({ form, editing }) => {
        return (
          <>
            <div className="flex flex-col gap-8">
              <div className="flex flex-row">
                <InfoKeyValue
                  className="w-1/2"
                  label="Name"
                  value={profile.fullNameOrEmail}
                  data-testid="profile-form-key-value-name"
                  edit={{
                    editing,
                    editModeComponent: (
                      <div className="flex w-1/2">
                        <FormInputField
                          className="w-1/2"
                          label="First name"
                          name="firstName"
                          form={form}
                          labelTestId="profile-form-label-first-name"
                          inputProps={{ 'data-testid': 'profile-form-input-first-name' }}
                        />
                        <FormInputField
                          className="w-1/2"
                          label="Last name"
                          name="lastName"
                          form={form}
                          labelTestId="profile-form-label-last-name"
                          inputProps={{ 'data-testid': 'profile-form-input-last-name' }}
                        />
                      </div>
                    ),
                  }}
                />
                <InfoKeyValue
                  className="w-1/2"
                  label="Display name for students"
                  value={profile.displayName || '-'}
                  data-testid="profile-form-key-value-display-name"
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormInputField
                        className="w-1/2"
                        label="Display name for students"
                        name="displayName"
                        form={form}
                        labelTestId="profile-form-label-display-name"
                        inputProps={{ 'data-testid': 'profile-form-input-display-name' }}
                      />
                    ),
                  }}
                />
              </div>
              <div className="flex flex-row">
                <InfoKeyValue
                  className="w-1/2"
                  label="Email"
                  value={profile.email}
                  data-testid="profile-form-key-value-email"
                />
                <InfoKeyValue
                  className="w-1/2"
                  label="Referral code"
                  value={profile.referralCode || '-'}
                  data-testid="profile-form-key-value-referral-code"
                />
              </div>
              <div className="flex flex-row">
                <InfoKeyValue
                  className="w-1/2"
                  label="Secondary email"
                  value={profile.secondaryEmail || '-'}
                  data-testid="profile-form-key-value-secondary-email"
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormInputField
                        className="w-1/2"
                        label="Secondary email"
                        name="secondaryEmail"
                        form={form}
                        labelTestId="profile-form-label-secondary-email"
                        inputProps={{ 'data-testid': 'profile-form-input-secondary-email' }}
                      />
                    ),
                  }}
                />
              </div>
              <div className="flex flex-row">
                <InfoKeyValue
                  className="w-1/2"
                  label="Phone number"
                  value={profile.phone || '-'}
                  data-testid="profile-form-key-value-phone"
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormInputField
                        className="w-1/2"
                        label="Phone number"
                        name="phone"
                        form={form}
                        labelTestId="profile-form-label-phone"
                        inputProps={{ 'data-testid': 'profile-form-input-phone' }}
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  className="w-1/2"
                  label="SMS text reminders"
                  value={
                    profile.smsReminderOptedIn ? (
                      <div className={`${smsReminderBaseClassnames} bg-green-100`}>
                        <CircleCheck className=" text-green-500" />
                      </div>
                    ) : (
                      <div className={`${smsReminderBaseClassnames} bg-red-100`}>
                        <CircleX className="text-red-500" />
                      </div>
                    )
                  }
                  data-testid="profile-form-key-value-sms-reminder"
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormCheckbox
                        className="w-1/2"
                        label="SMS text reminders"
                        name="smsReminderOptedIn"
                        form={form}
                        labelTestId="profile-form-label-sms-reminder"
                        checkboxProps={{ 'data-testid': 'profile-form-checkbox-sms-reminder' }}
                      />
                    ),
                  }}
                />
              </div>
              <div className="flex flex-row">
                <InfoKeyValue
                  className="w-1/2"
                  label="Time zone"
                  value={profile.timeZone || '-'}
                  data-testid="profile-form-key-value-timezone"
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormComboBox
                        className="w-1/2"
                        name="timeZone"
                        label="Time zone"
                        form={form}
                        {...comboBoxOptionsOf(getTimezoneTypeahead)}
                        labelTestId="profile-form-label-timezone"
                        triggerProps={{ className: 'w-2/3', 'data-testid': 'profile-form-select-timezone' }}
                        required
                        formDescription={
                          <div className="w-2/3">
                            <span className="font-semibold">Note:</span> Changing your time zone will move your
                            preferred schedule to the new time zone. Please verify your schedule{' '}
                            <Link className="font-bold" to="/my-availability" search={{ schedule: 'regular' }}>
                              here
                            </Link>{' '}
                            after making time zone changes. This will not affect your classes that are already
                            scheduled.
                          </div>
                        }
                      />
                    ),
                  }}
                />
              </div>
            </div>
          </>
        );
      }}
    </EditableInfoCard>
  );
};
